import { useQueryClient } from '@tanstack/react-query';
import {
  CARCASS_ANIMALS,
  CARCASS_TYPES,
  lineTotalCents,
  purchaseSchema,
  shelfLifeFor,
  type CarcassAnimal,
  type ProductDTO,
  type PurchaseDetailDTO,
} from '@bifeai/shared';
import { Button, Callout, Card, CardBody, CardHeader, Dialog, Field, Input, PageHeader, Select, Textarea, useToast } from '@bifeai/ui';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage, isApiError } from '../../lib/api';
import { invalidateInventory, useProducts, useSuppliers, zodFieldErrors } from '../../lib/catalog';
import { formatMoney } from '../../lib/format';
import { usePosSettings } from '../../lib/customers';
import { useSession } from '../../lib/session';
import { addDaysIso, pesosToCents, priceUnit, toInternalQty, todayIso } from '../../lib/units';
import { SupplierDialog } from '../SuppliersPage';

interface Line {
  key: number;
  productId: string;
  qty: string;
  cost: string;
  expiresAt: string;
}

let nextKey = 1;
const emptyLine = (): Line => ({
  key: nextKey++,
  productId: '',
  qty: '',
  cost: '',
  expiresAt: '',
});

const INVOICE_TYPES = ['Factura A', 'Factura B', 'Factura C', 'Remito', 'Ticket', 'Otro'];

export function NewPurchasePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [params] = useSearchParams();
  const { me, isHeadquartersUser, selectedBranchId, multiBranch } = useSession();
  const suppliers = useSuppliers();
  const products = useProducts();
  const posSettings = usePosSettings();

  const defaultBranch = isHeadquartersUser
    ? (selectedBranchId ?? me.branches.find((b) => b.isHeadquarters)?.id ?? me.branches[0]?.id ?? '')
    : (me.membership.branchId ?? '');

  const [supplierId, setSupplierId] = useState(params.get('proveedor') ?? '');
  const [branchId, setBranchId] = useState(defaultBranch);
  const [date, setDate] = useState(todayIso());
  const [invoiceType, setInvoiceType] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Line[]>(() => [emptyLine()]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newSupplier, setNewSupplier] = useState(false);
  // Medias reses: se eligen como cualquier producto; si el tipo no existe, se crea solo (se desposta en Desposte, manual o automático).
  const [otherCarcass, setOtherCarcass] = useState<{ lineKey: number; name: string; animal: CarcassAnimal } | null>(null);
  const [carcassBusy, setCarcassBusy] = useState(false);

  const productById = useMemo(() => new Map((products.data ?? []).map((p) => [p.id, p])), [products.data]);
  const grouped = useMemo(() => {
    // Las medias reses van aparte (primero), solo con el adicional de desposte.
    const groups = new Map<string, ProductDTO[]>();
    for (const p of products.data ?? []) {
      if (p.isCarcass) continue;
      const k = p.categoryName ?? 'Sin categoría';
      groups.set(k, [...(groups.get(k) ?? []), p]);
    }
    return [...groups.entries()];
  }, [products.data]);

  const carcassOptions = useMemo(() => {
    const existing = (products.data ?? []).filter((p) => p.isCarcass);
    const names = new Set(existing.map((p) => p.name.toLowerCase()));
    const standard = CARCASS_ANIMALS.flatMap((a) => CARCASS_TYPES[a].map((n) => ({ animal: a, name: n }))).filter(
      (x) => !names.has(x.name.toLowerCase()),
    );
    return [
      ...existing.map((p) => ({ value: p.id, label: p.name })),
      ...standard.map((x) => ({ value: `new:${x.animal}:${x.name}`, label: x.name })),
    ].sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [products.data]);

  /** Elige una media res que todavía no existe: se crea el tipo (con su lista de cortes) y queda elegida en el renglón. */
  async function pickNewCarcass(lineKey: number, name: string, animal: CarcassAnimal) {
    setCarcassBusy(true);
    try {
      const r = await api.post<{ productId: string; name: string }>('/cutting/carcass-products', { name, animal });
      await qc.invalidateQueries({ queryKey: ['products'] });
      await qc.invalidateQueries({ queryKey: ['cutting'] });
      const life = posSettings.data ? shelfLifeFor({ shelfLifeDays: null, kind: 'MEAT', animal }, posSettings.data.stock) : null;
      update(lineKey, { productId: r.productId, ...(life ? { expiresAt: addDaysIso(todayIso(), life) } : {}) });
      setOtherCarcass(null);
    } catch (err) {
      toast.error('No se pudo', errorMessage(err));
    } finally {
      setCarcassBusy(false);
    }
  }

  const supplier = suppliers.data?.find((s) => s.id === supplierId) ?? null;
  const autoDue = supplier && supplier.paymentTermsDays > 0 ? addDaysIso(date, supplier.paymentTermsDays) : '';
  const effectiveDue = dueDate ?? autoDue;

  const computed = lines.map((l) => {
    const p = productById.get(l.productId);
    const qty = p && l.qty.trim() ? toInternalQty(p.unitType, l.qty) : null;
    const cost = l.cost.trim() ? pesosToCents(l.cost) : null;
    const total = p && qty !== null && cost !== null ? lineTotalCents(p.unitType, qty, cost) : null;
    return { line: l, product: p, qty, cost, total };
  });
  const total = computed.reduce((a, c) => a + (c.total ?? 0), 0);

  const update = (key: number, patch: Partial<Line>) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    // Al corregir un renglón se limpian sus errores.
    setErrors((e) => {
      const fields = Object.keys(patch).map((f) => `line.${key}.${f === 'cost' ? 'unitCostCents' : f}`);
      if (!fields.some((f) => f in e)) return e;
      const next = { ...e };
      for (const f of fields) delete next[f];
      return next;
    });
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const local: Record<string, string> = {};
    const used = computed.filter((c) => c.line.productId || c.line.qty || c.line.cost);
    used.forEach((c, i) => {
      if (!c.product) local[`items.${i}.productId`] = 'Elegí un producto';
      if (c.qty === null || c.qty <= 0) local[`items.${i}.qty`] = 'Cantidad inválida';
      if (c.cost === null) local[`items.${i}.unitCostCents`] = 'Costo inválido';
      if (c.product?.tracksExpiry && !c.product.isCarcass && !c.line.expiresAt) local[`items.${i}.expiresAt`] = 'Falta el vencimiento';
    });
    const payload = {
      supplierId,
      branchId,
      date,
      invoiceType: invoiceType || null,
      invoiceNumber,
      dueDate: effectiveDue || null,
      notes,
      items: used.map((c) => ({
        productId: c.line.productId,
        qty: c.qty ?? 0,
        unitCostCents: c.cost ?? 0,
        expiresAt: c.line.expiresAt || null,
      })),
    };
    const parsed = purchaseSchema.safeParse(payload);
    const all = {
      ...(parsed.success ? {} : zodFieldErrors(parsed.error)),
      ...local,
    };
    if (!supplierId) all.supplierId = 'Elegí el proveedor';
    // Los errores de la API vienen por posición entre los renglones cargados: se reubican.
    setErrors(
      remap(
        all,
        used.map((c) => c.line.key),
      ),
    );
    if (Object.keys(all).length > 0) {
      if (all.items) setFormError(all.items);
      return;
    }
    setSaving(true);
    try {
      const saved = await api.post<PurchaseDetailDTO>('/purchases', payload);
      toast.success('Compra cargada', `${formatMoney(saved.totalCents)} — el stock ya se actualizó.`);
      await invalidateInventory(qc);
      navigate(`/compras/${saved.id}`, { replace: true });
    } catch (err) {
      if (isApiError(err) && err.fields)
        setErrors(
          remap(
            err.fields,
            used.map((c) => c.line.key),
          ),
        );
      setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const lineError = (key: number, field: string) => errors[`line.${key}.${field}`];
  const noProducts = products.isSuccess && products.data.length === 0;

  return (
    <>
      <PageHeader
        title="Nueva compra"
        subtitle="Cargá la factura o el remito. Cada renglón entra al stock como un lote con su costo y vencimiento."
        actions={
          <Link to="/compras">
            <Button variant="ghost" icon={<ArrowLeft />}>
              Volver a compras
            </Button>
          </Link>
        }
      />

      {noProducts && (
        <Callout tone="warning">
          Todavía no hay productos. <Link to="/productos">Cargalos primero</Link> para poder registrar compras.
        </Callout>
      )}

      <form className="bf-form" onSubmit={submit} noValidate style={{ gap: 20 }}>
        {formError && <Callout tone="danger">{formError}</Callout>}

        <Card>
          <CardHeader title="Datos de la compra" />
          <CardBody>
            <div className="bf-form">
              <div className="bf-form-row">
                <Field label="Proveedor" error={errors.supplierId}>
                  <Select
                    value={supplierId}
                    onChange={(e) => {
                      if (e.target.value === '__new') return setNewSupplier(true);
                      setSupplierId(e.target.value);
                    }}
                  >
                    <option value="">Elegí un proveedor</option>
                    {suppliers.data?.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                    <option value="__new">+ Nuevo proveedor…</option>
                  </Select>
                </Field>
                {isHeadquartersUser && multiBranch ? (
                  <Field label="Sucursal que recibe" error={errors.branchId}>
                    <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                      {me.branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null}
                <Field label="Fecha" error={errors.date}>
                  <Input type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
                </Field>
              </div>
              <div className="bf-form-row">
                <Field label="Comprobante" error={errors.invoiceType}>
                  <Select value={invoiceType} onChange={(e) => setInvoiceType(e.target.value)}>
                    <option value="">Sin comprobante</option>
                    {INVOICE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Número" error={errors.invoiceNumber}>
                  <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="0001-00012345" />
                </Field>
                <Field
                  label="Vencimiento del pago"
                  error={errors.dueDate}
                  hint={
                    supplier
                      ? supplier.paymentTermsDays > 0
                        ? `Plazo del proveedor: ${supplier.paymentTermsDays} días.`
                        : 'Proveedor de contado.'
                      : undefined
                  }
                >
                  <Input type="date" value={effectiveDue} min={date} onChange={(e) => setDueDate(e.target.value)} />
                </Field>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Productos" subtitle="Los productos por kilo se cargan en kilos; el costo es por kilo o por unidad." />
          <CardBody>
            <div className="bf-lines">
              <div className="bf-line bf-line-head" aria-hidden>
                <span>Producto</span>
                <span>Cantidad</span>
                <span>Costo</span>
                <span>Vence</span>
                <span style={{ textAlign: 'right' }}>Subtotal</span>
                <span />
              </div>
              {computed.map(({ line, product, total: lineTotal }, idx) => {
                const unit = product ? (product.unitType === 'WEIGHT' ? 'kg' : 'u.') : '';
                return (
                  <div className="bf-line" key={line.key}>
                    <div>
                      <Select
                        aria-label={`Producto del renglón ${idx + 1}`}
                        value={line.productId}
                        aria-invalid={Boolean(lineError(line.key, 'productId')) || undefined}
                        disabled={carcassBusy}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === 'other-carcass') return setOtherCarcass({ lineKey: line.key, name: 'Media res ', animal: 'BEEF' });
                          if (v.startsWith('new:')) {
                            const [, a, ...rest] = v.split(':');
                            return void pickNewCarcass(line.key, rest.join(':'), a as CarcassAnimal);
                          }
                          // Vencimiento sugerido: hoy + los días de vida del producto o de su tipo (Configuración → Vencimientos).
                          const p = productById.get(e.target.value);
                          const life = p && p.tracksExpiry && posSettings.data ? shelfLifeFor(p, posSettings.data.stock) : null;
                          update(line.key, {
                            productId: e.target.value,
                            ...(life && !line.expiresAt ? { expiresAt: addDaysIso(todayIso(), life) } : {}),
                          });
                        }}
                      >
                        <option value="">Elegí un producto</option>
                        <optgroup label="Medias reses (para despostar)">
                          {carcassOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                          <option value="other-carcass">Otra media res…</option>
                        </optgroup>
                        {grouped.map(([cat, ps]) => (
                          <optgroup key={cat} label={cat}>
                            {ps.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </Select>
                      {lineError(line.key, 'productId') && <p className="bf-field-error">{lineError(line.key, 'productId')}</p>}
                    </div>
                    <div>
                      <Input
                        aria-label={`Cantidad del renglón ${idx + 1}${unit ? ` en ${unit}` : ''}`}
                        inputMode="decimal"
                        placeholder={unit ? `0 ${unit}` : 'Cantidad'}
                        value={line.qty}
                        aria-invalid={Boolean(lineError(line.key, 'qty')) || undefined}
                        onChange={(e) => update(line.key, { qty: e.target.value })}
                      />
                      {lineError(line.key, 'qty') && <p className="bf-field-error">{lineError(line.key, 'qty')}</p>}
                    </div>
                    <div>
                      <Input
                        aria-label={`Costo del renglón ${idx + 1}`}
                        inputMode="decimal"
                        placeholder={product ? `$ ${priceUnit(product.unitType)}` : '$'}
                        value={line.cost}
                        aria-invalid={Boolean(lineError(line.key, 'unitCostCents')) || undefined}
                        onChange={(e) => update(line.key, { cost: e.target.value })}
                      />
                      {lineError(line.key, 'unitCostCents') && <p className="bf-field-error">{lineError(line.key, 'unitCostCents')}</p>}
                    </div>
                    <div>
                      <Input
                        type="date"
                        aria-label={`Vencimiento del renglón ${idx + 1}`}
                        value={line.expiresAt}
                        min={date}
                        disabled={product ? !product.tracksExpiry : false}
                        title={product && !product.tracksExpiry ? 'Este producto no controla vencimiento' : undefined}
                        aria-invalid={Boolean(lineError(line.key, 'expiresAt')) || undefined}
                        onChange={(e) => update(line.key, { expiresAt: e.target.value })}
                      />
                      {lineError(line.key, 'expiresAt') && <p className="bf-field-error">{lineError(line.key, 'expiresAt')}</p>}
                    </div>
                    <div className="bf-line-total">{lineTotal !== null ? formatMoney(lineTotal) : '—'}</div>
                    <button
                      type="button"
                      className="bf-icon-btn"
                      aria-label={`Quitar renglón ${idx + 1}`}
                      disabled={lines.length === 1}
                      onClick={() => setLines((ls) => ls.filter((l) => l.key !== line.key))}
                    >
                      <Trash2 />
                    </button>
                  </div>
                );
              })}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  gap: 16,
                  flexWrap: 'wrap',
                }}
              >
                <Button icon={<Plus />} onClick={() => setLines((ls) => [...ls, emptyLine()])} disabled={lines.length >= 200}>
                  Agregar producto
                </Button>
                <div className="bf-totals">
                  <span className="bf-cell-sub">Total de la compra</span>
                  <span className="bf-totals-main">{formatMoney(total)}</span>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Field label="Notas" error={errors.notes}>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opcional" />
            </Field>
          </CardBody>
        </Card>

        <div className="bf-form-actions">
          <Button onClick={() => navigate('/compras')} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={saving} disabled={noProducts}>
            Confirmar compra
          </Button>
        </div>
      </form>

      {otherCarcass && (
        <Dialog
          open
          onOpenChange={(o) => !o && !carcassBusy && setOtherCarcass(null)}
          title="Otra media res"
          description="Escribí cómo le decís (ej.: Media res vaca conserva). Arranca con la lista de cortes estándar; después la ajustás en Desposte."
        >
          <form
            className="bf-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (otherCarcass.name.trim().length >= 3) void pickNewCarcass(otherCarcass.lineKey, otherCarcass.name.trim(), otherCarcass.animal);
            }}
          >
            <Field label="Nombre">
              <Input value={otherCarcass.name} onChange={(e) => setOtherCarcass({ ...otherCarcass, name: e.target.value })} autoFocus />
            </Field>
            <Field label="Animal">
              <Select value={otherCarcass.animal} onChange={(e) => setOtherCarcass({ ...otherCarcass, animal: e.target.value as CarcassAnimal })}>
                <option value="BEEF">Vacuno</option>
                <option value="PORK">Cerdo</option>
              </Select>
            </Field>
            <div className="bf-form-actions">
              <Button onClick={() => setOtherCarcass(null)} disabled={carcassBusy}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" loading={carcassBusy} disabled={otherCarcass.name.trim().length < 3}>
                Usar esta media res
              </Button>
            </div>
          </form>
        </Dialog>
      )}

      {newSupplier && (
        <SupplierDialog
          supplier={null}
          onClose={() => setNewSupplier(false)}
          onSaved={async (s) => {
            await qc.invalidateQueries({ queryKey: ['suppliers'] });
            setSupplierId(s.id);
            setNewSupplier(false);
            toast.success('Proveedor creado', s.name);
          }}
        />
      )}
    </>
  );
}

/** "items.2.qty" → "line.<key del renglón>.qty" (y deja igual el resto de los campos). */
function remap(fields: Record<string, string>, keys: number[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(fields)) {
    const m = /^items\.(\d+)\.(\w+)$/.exec(k);
    const key = m ? keys[Number(m[1])] : undefined;
    out[m && key !== undefined ? `line.${key}.${m[2]}` : k] = v;
  }
  return out;
}
